import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ZoneRowsEditor from "@/components/patients/ZoneRowsEditor";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toLocalIsoString } from "@/lib/agendaConstants";
import { TREATMENTS } from "@/constants/testIds";

const PAYMENT_METHODS = [
  { value: "cash", label: "Efectivo" },
  { value: "card", label: "Tarjeta" },
  { value: "transfer", label: "Transferencia" },
  { value: "zelle", label: "Zelle" },
  { value: "other", label: "Otro" },
];

/** Retoque/procedimiento adicional vinculado a un tratamiento original (ej:
 * unidades extra durante la revision). Puede ir sin cargo (dentro de la
 * revision incluida del servicio) o generar un cobro aparte. */
export default function TouchUpModal({ open, onOpenChange, patientId, treatment, professionals, products, currentUser, onCreated }) {
  const isProfessional = currentUser?.role === "professional";
  const canUseReview = treatment?.review_status === "active";
  const EMPTY_FORM = {
    professional_id: isProfessional ? currentUser.professional_id || "" : "",
    observations: "",
    touchup_date: toLocalIsoString(new Date()),
    included_in_review: false,
  };
  const [form, setForm] = useState(EMPTY_FORM);
  const [zones, setZones] = useState([]);
  const [primaryProductId, setPrimaryProductId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("none");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setZones([]);
      setPrimaryProductId("");
      setPaymentMethod("none");
      setPaymentAmount("");
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = async () => {
    if (!form.professional_id) {
      setError("Selecciona el profesional que aplico el retoque");
      return;
    }
    const validZones = zones.filter((z) => z.zone_name && Number(z.units) > 0);
    setSaving(true);
    setError("");
    try {
      const payload = {
        professional_id: form.professional_id,
        zones: validZones.map((z) => ({ zone_name: z.zone_name, units: Number(z.units), notes: z.notes || null })),
        primary_product_id: primaryProductId || null,
        observations: form.observations || null,
        touchup_date: form.touchup_date,
        included_in_review: form.included_in_review,
        new_payment: !form.included_in_review && paymentMethod !== "none" && paymentAmount ? { method: paymentMethod, amount: Number(paymentAmount) } : null,
      };
      const { data } = await api.post(`/patients/${patientId}/treatments/${treatment.id}/touchups`, payload);
      onCreated(data);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={TREATMENTS.touchUpModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Registrar retoque</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Profesional que lo aplico</Label>
            <Select value={form.professional_id} onValueChange={(v) => setForm((f) => ({ ...f, professional_id: v }))} disabled={isProfessional}>
              <SelectTrigger><SelectValue placeholder="Seleccionar profesional" /></SelectTrigger>
              <SelectContent>
                {professionals.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Fecha y hora</Label>
            <Input type="datetime-local" value={form.touchup_date} onChange={(e) => setForm((f) => ({ ...f, touchup_date: e.target.value }))} />
          </div>

          <ZoneRowsEditor
            zones={zones}
            setZones={setZones}
            primaryProductId={primaryProductId}
            setPrimaryProductId={setPrimaryProductId}
            products={products || []}
            zoneCatalog={[]}
            suggestedZoneNames={(treatment?.zones || []).map((z) => z.zone_name).filter(Boolean)}
          />

          <div className="border-t border-border pt-4 space-y-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={form.included_in_review}
                disabled={!canUseReview}
                onCheckedChange={(v) => setForm((f) => ({ ...f, included_in_review: Boolean(v) }))}
                data-testid={TREATMENTS.touchUpIncludedCheckbox}
              />
              Incluido en la revisión sin cargo
            </label>
            {!canUseReview && (
              <p className="text-xs text-muted-foreground">
                {treatment?.review_status === "used" ? "La revisión incluida de este tratamiento ya fue utilizada." :
                 treatment?.review_status === "expired" ? "La revisión incluida de este tratamiento ya venció." :
                 "Este tratamiento no tiene una revisión sin cargo configurada."}
              </p>
            )}

            {!form.included_in_review && (
              <div>
                <Label className="text-sm text-muted-foreground mb-1 block">Cobro por este retoque (opcional)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin cobro</SelectItem>
                      {PAYMENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="number" min="0" step="0.01" placeholder="Monto" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} disabled={paymentMethod === "none"} />
                </div>
              </div>
            )}
          </div>

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Observaciones</Label>
            <Textarea value={form.observations} onChange={(e) => setForm((f) => ({ ...f, observations: e.target.value }))} rows={3} />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={TREATMENTS.touchUpCancelButton}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid={TREATMENTS.touchUpSaveButton}>
            {saving ? "Guardando..." : "Registrar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
